import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserProfile } from './useUserProfile';
import { toast } from 'sonner';

export interface CustomerSaas {
  id: string;
  tenant_id: string;
  product_id: string | null;
  name: string;
  email: string | null;
  document: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  plan_name: string | null;
  monthly_value: number | null;
  subscription_start_date: string | null;
  cancellation_date: string | null;
  status: 'active' | 'cancelled' | 'trial' | 'pending';
  gateway_customer_id: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Novos campos do lifecycle (calculados via transações)
  first_purchase_date: string | null;
  last_transaction_date: string | null;
  total_renewals: number;
  renewal_rate: number | null;
  // 🆕 Relacionamento com produto
  product?: {
    id: string;
    name: string;
  } | null;
  // 🆕 Dados da última transação para cálculo de status
  last_transaction?: {
    payment_date: string;
    period_end_date: string | null;
    product_plan: {
      id: string;
      recurrence_type: string;
      name: string;
    } | null;
  }[];
}

export function useCustomersSaas() {
  const { user } = useAuth();
  const { tenant } = useUserProfile();
  const queryClient = useQueryClient();

  const { data: customers, isLoading } = useQuery({
    queryKey: ['customers-saas', tenant?.id],
    queryFn: async () => {
      if (!tenant?.id) return [];
      
      const { data, error } = await supabase
        .from('customers_saas')
        .select(`
          *,
          product:products!product_id(
            id,
            name
          ),
          last_transaction:transactions!customer_saas_id(
            payment_date,
            period_end_date,
            product_plan:product_plans!product_plan_id(
              id,
              recurrence_type,
              name
            )
          )
        `)
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .order('payment_date', { 
          foreignTable: 'transactions',
          ascending: false 
        })
        .limit(1, { foreignTable: 'transactions' })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as CustomerSaas[];
    },
    enabled: !!tenant?.id,
  });

  const createCustomer = useMutation({
    mutationFn: async (customer: Omit<CustomerSaas, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'tenant_id' | 'is_active' | 'first_purchase_date' | 'last_transaction_date' | 'total_renewals' | 'renewal_rate'>) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      const { error } = await supabase
        .from('customers_saas')
        .insert({
          ...customer,
          tenant_id: tenant.id,
          created_by: user?.id,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers-saas'] });
      toast.success('Cliente criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar cliente: ' + error.message);
    },
  });

  const updateCustomer = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CustomerSaas> }) => {
      const { error } = await supabase
        .from('customers_saas')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers-saas'] });
      toast.success('Cliente atualizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao atualizar cliente: ' + error.message);
    },
  });

  const deleteCustomer = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('customers_saas')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers-saas'] });
      toast.success('Cliente desativado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error('Erro ao desativar cliente: ' + error.message);
    },
  });

  const findOrCreateCustomerSaas = useMutation({
    mutationFn: async ({ 
      customerName, 
      productId 
    }: { 
      customerName: string; 
      productId: string; 
    }) => {
      if (!tenant?.id) throw new Error('Tenant não encontrado');
      
      // 1. Buscar todos os customer_saas com esse nome
      const { data: existingCustomers } = await supabase
        .from('customers_saas')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('name', customerName)
        .eq('is_active', true);
      
      // 2. Verificar se já existe para esse produto
      const existing = existingCustomers?.find(c => c.product_id === productId);
      
      if (existing) {
        return existing.id; // Retorna ID existente
      }
      
      // 3. Se não existe, buscar dados base do primeiro registro
      const baseCustomer = existingCustomers?.[0];
      
      if (!baseCustomer) {
        throw new Error('Cliente não encontrado. Crie o cliente primeiro.');
      }
      
      // 4. Criar novo customer_saas para o produto
      const { data: newCustomer, error } = await supabase
        .from('customers_saas')
        .insert({
          tenant_id: tenant.id,
          name: customerName,
          email: baseCustomer.email,
          document: baseCustomer.document,
          phone: baseCustomer.phone,
          city: baseCustomer.city,
          state: baseCustomer.state,
          product_id: productId,
          status: 'active',
          created_by: user?.id,
        })
        .select('id')
        .single();
      
      if (error) throw error;
      
      return newCustomer.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers-saas'] });
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar/buscar cliente: ' + error.message);
    },
  });

  return {
    customers,
    isLoading,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    findOrCreateCustomerSaas,
  };
}
